<template>
    <div class="flex items-center relative">
        <label v-if="label" class="mr-2 text-sm font-medium ct-text-1">{{ label }}</label>
        <div class="w-full relative rounded-lg shadow-sm shadow-gray-900/5">
            <div
                class="ct-input--select"
                :class="{
                    'is-disabled': disabled,
                    'is-error': isInvalid,
                }"
                :style="{
                    'background-color': bgColor,
                    '--tw-ring-color': borderColor,
                    color: textColor,
                    'border-radius': borderRadius,
                }"
                @click="show"
            >
                <span v-if="!searchEnabled || !expanded" class="block truncate">{{
                    selectedLabel || title || "Select"
                }}</span>
                <input
                    v-if="expanded && searchEnabled"
                    ref="searchInput"
                    v-model="search"
                    class="block w-full border-0 p-0 ct-text-1 placeholder:ct-text-placeholder focus:ring-0 text-base bg-transparent"
                    type="text"
                    placeholder="Search..."
                    @keydown.up="moveIndexUp"
                    @keydown.down="moveIndexDown"
                    @keyup.enter="selectCurrentOption"
                    @keyup.esc="hide"
                />
                <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDownIcon
                        class="h-5 w-5 transform transition-transform"
                        :class="{
                            'text-white': !isInvalid && transparent,
                            'text-gray-400': !isInvalid && !transparent,
                            'text-red-300': isInvalid,
                            'rotate-180': expanded,
                        }"
                    />
                </span>

                <div v-if="tooltip" class="absolute inset-y-0 right-4 pr-3 flex items-center">
                    <HelpIcon
                        v-tooltip="{ content: tooltip, autoHide: false }"
                        class="h-5 w-5 text-gray-400 cursor-help"
                    />
                </div>
            </div>
        </div>
        <transition
            leave-active-class="transition ease-in duration-100"
            leave-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <ul
                v-if="expanded && availableOptions.length"
                ref="list"
                v-click-outside="hide"
                class="absolute top-full z-10 mt-1 w-full shadow-lg max-h-60 py-1 text-base ring-1 ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                :style="{
                    'background-color': bgColor,
                    '--tw-ring-color': borderColor,
                    color: textColor,
                    'border-radius': borderRadius,
                }"
            >
                <template v-for="(option, index) in availableOptions" :key="option.value">
                    <SelectOption
                        :ref="`option-${index}`"
                        :option="option"
                        :selected="value == option.value"
                        :highlighted="searchEnabled && selectedIndex === index"
                        @mouseenter="selectedIndex = index"
                        @select="select"
                        :bgColor="bgColor"
                        :textColor="textColor"
                        :borderColor="borderColor"
                    />
                </template>
            </ul>
        </transition>
    </div>
</template>

<script>
import SelectOption from "./SelectOption.vue";
import { ChevronDownIcon, HelpIcon } from "@easy/ui/icons";

export default {
    components: {
        ChevronDownIcon,
        HelpIcon,
        SelectOption,
    },
    props: {
        label: {
            type: String,
            default: "",
        },
        title: {
            type: String,
            default: "",
        },
        tooltip: {
            type: String,
            default: "",
        },
        options: {
            type: Array,
            default: () => [],
        },
        value: {
            type: [String, Number],
            default: "",
        },
        disabled: Boolean,
        isInvalid: Boolean,
        transparent: Boolean,
        searchEnabled: {
            type: Boolean,
            default: true,
        },
        bgColor: {
            type: String,
            default: "",
        },
        textColor: {
            type: String,
            default: "",
        },
        borderColor: {
            type: String,
            default: "",
        },
        borderRadius: {
            type: String,
            default: "",
        },
    },
    emits: ["input", "change"],
    data() {
        return {
            selfValue: this.value,
            expanded: false,
            search: "",
            selectedIndex: 0,
        };
    },
    computed: {
        selectedLabel() {
            if (this.value === undefined) {
                return null;
            }

            const option = this.options.find((option) => option.value == this.value);

            return option ? option.label : null;
        },
        availableOptions() {
            if (!this.search) {
                return this.options;
            }

            return this.options.filter(
                (option) =>
                    !option.isDivider &&
                    option.label.toLowerCase().includes(this.search.toLowerCase())
            );
        },
    },
    watch: {
        expanded(value) {
            if (value && this.searchEnabled) {
                this.$nextTick(() => {
                    this.$refs.searchInput.focus();
                });
            } else {
                this.search = "";
            }
        },
        availableOptions() {
            this.selectedIndex = 0;
        },
    },
    methods: {
        hide(event) {
            if (event) {
                event.stopImmediatePropagation();
            }

            this.expanded = false;
        },
        show() {
            if (!this.disabled) {
                this.expanded = true;
            }
        },
        select(value) {
            this.$emit("input", value);
            this.$emit("change", value);
            this.hide();
        },
        selectCurrentOption() {
            const option = this.availableOptions[this.selectedIndex];

            this.select(option ? option.value : null);
        },
        moveIndexUp() {
            this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
            this.scrollList();
        },
        moveIndexDown() {
            this.selectedIndex = Math.min(this.selectedIndex + 1, this.availableOptions.length - 1);
            this.scrollList();
        },
        scrollList() {
            const option = this.$refs[`option-${this.selectedIndex}`];

            if (option[0]) {
                option[0].$el.scrollIntoView({
                    block: "nearest",
                });
            }
        },
    },
};
</script>
